import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import Avatar from "../Avatar";
import { Code, Text } from "../catalyst/text";
import { AgGridReact } from "ag-grid-react";
import useThemeStore, { isDarkMode } from "../../store/themeStore";
import { exportToCsv, getGolfPlayerCategory } from "../../utils/appUtils";
import { formatDateToYYYYMMDDHHMM, showDateShort } from "../../utils/dateUtils";
import { useSeasonsStore } from "../../store/seasonsStore";
import { useEffect, useState } from "react";
import { ColDef } from "ag-grid-community";
import useAuthStore from "../../store/authStore";
import { UserRoles } from "../../models/playerModel";
import { toast } from "react-toastify";
import { Button } from "../catalyst/button";
import { Download } from "lucide-react";
import { convertToHandicapDetails } from "../../utils/courseUtils";

const ToolTipComponent = (props: any) => {
  if (props.location === "header") {
    const course = JSON.parse(props.value);
    return (
      <div className="bg-white dark:bg-gray-900 p-3 rounded-md shadow-md">
        <Text className="font-bold">{course.courseName}</Text>
        <Text className="font-extrabold">
          Slope Rating: M: {course.slopeRating.M}, S:{course.slopeRating.S}, L:
          {course.slopeRating.L}
        </Text>
        <Text className="font-extrabold">
          Course Rating: M:{course.courseRating.M}, S:{course.courseRating.S},
          L:{course.courseRating.L}
        </Text>

        <Text className="font-extrabold">Course Par: {course.coursePar}</Text>

        <Code className="font-extrabold">
          (Handicap x Slope Rating / 113) + (Course Rating - Course Par) * 75%
        </Code>
        <Text>and rounded to the nearest whole number</Text>
      </div>
    );
  }
  return (
    <div className="bg-white dark:bg-gray-900 p-3 rounded-md shadow-md">
      <Text className="font-extrabold">{props.value}</Text>
      <Text>and rounded to the nearest whole number.</Text>
    </div>
  );
};


export const PlayersTable: React.FC<any> = ({ players, setSearch }) => {
  const isDark = useThemeStore(isDarkMode);
  const { currentSeason } = useSeasonsStore();
  const { currentUser } = useAuthStore();

  const [showPreferredDates, setShowPreferredDates] = useState(false);

  const userCellRenderer = (params: any) => {
    const person = params.data;
    return (
      <div className="flex items-center h-full">
        <div className="h-11 w-11 flex-shrink-0">
          <Avatar
            firstName={person.firstName}
            lastName={person.lastName}
            imageUrl={`/api/static/images/${person.profilePicture}`}
            className="h-11 w-11 rounded-full"
          />
        </div>
        <div className="ml-4 leading-none gap-2 flex flex-col">
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 text-ellipsis w-full">
            {person.fullName}
          </div>
        </div>
      </div>
    );
  };
  const initialColumnDefs: ColDef[] = [
    {
      headerName: "Player",
      field: "fullName",
      cellRenderer: userCellRenderer,
      flex: 2,
      minWidth: 250,
    },
    {
      headerName: "Role",
      valueGetter: (params: any) => {
        return params.data.role || params.data.playerRole;
      },
        },
        {
      headerName: "HI",
      maxWidth: 100,
      valueGetter: (params: any) => {
        return params.data.seasonHandicap ?? params.data.playerHandicap;
      },
      type: "numericColumn",
      comparator: (valueA: any, valueB: any) => {
        return valueA - valueB;
      },
    },
    { headerName: "Home Club", field: "homeClub" },
    {
      headerName: "M/S/L",
      field: "category",
      maxWidth: 120,
    },
    {
      headerName: "Core Member",
      field: "isCoreMember",
      cellRenderer: (params: any) => {
        return params.value ? "Yes" : "No";
      },
      maxWidth: 180,
    },
    {
      headerName: "Team Name",
      valueGetter: (params: any) => {
        const {
          playerForTeamName,
          coreMemberForTeamName,
          captainForTeamName,
          ownerForTeamName,
        } = params.data;
        return (
          playerForTeamName ||
          coreMemberForTeamName ||
          captainForTeamName ||
          ownerForTeamName ||
          ""
        );
      },
      cellRenderer: (params: any) => {
        return params.value;
      },
      flex: 1,
      minWidth: 150,
      comparator: (valueA: string, valueB: string) => {
        if (valueA === valueB) return 0;
        if (valueA === "N/A") return 1;
        if (valueB === "N/A") return -1;
        return valueA.localeCompare(valueB);
      },
    },
  ];

  const [columnDefs, setColumnDefs] = useState<ColDef[]>(initialColumnDefs);

  const defaultColDef = {
    flex: 1,
    resizable: true,
    sortable: true,
    filter: true,
  };

  const getCoursePHColdef = (course: any) => {
    return {
      headerName: `${course.courseName} (PH)`,
      field: course.courseName.toLowerCase().replace(/\s+/g, ""),
      tooltipComponent: ToolTipComponent,
      headerTooltip: JSON.stringify(course),
      tooltipValueGetter: (params: any) => {
        const hcp = params.data.seasonHandicap ?? params.data.playerHandicap;
        if (!hcp) {
          return "";
        }
        const cat = params.data.category;
        if (cat === "M") {
          return `(${hcp} x ${course.slopeRating.M} / 113) + ( ${course.courseRating.M} - ${course.coursePar}) * 75%`;
        } else if (cat === "S") {
          return `(${hcp} x ${course.slopeRating.S} / 113) + ( ${course.courseRating.S} - ${course.coursePar}) * 75%`;
        } else if (cat === "L") {
          return `(${hcp} x ${course.slopeRating.L} / 113) + ( ${course.courseRating.L} - ${course.coursePar}) * 75%`;
        } else {
          return "";
        }
      },
    };
  }


  useEffect(() => {
    let newColDefs = [...initialColumnDefs];
    if (
      currentSeason &&
      currentSeason.courseConfigs &&
      currentSeason.courseConfigs.length > 0
    ) {
      currentSeason.courseConfigs.forEach((c) => {
        const course = convertToHandicapDetails(c);
        newColDefs = [...newColDefs, getCoursePHColdef(course)];
      });
    }
    if (currentSeason && showPreferredDates) {
      const prefDates = currentSeason.preferredDates || [];
      const allDatesColumn = {
        headerName: "All Dates",
        cellRenderer: (params: any) => {
          const playerDates = params.data.preferredDates || [];
          const allDatesSelected = prefDates.every((date) =>
            playerDates.includes(date)
          );
          return (
            <input
              type="checkbox"
              checked={allDatesSelected}
              disabled
              className="cursor-not-allowed"
            />
          );
        },
        filter: false,
        sortable: false,
        flex: 1,
        minWidth: 100,
      };
      newColDefs = [...newColDefs, allDatesColumn];
      const dateColumns = prefDates.map((date) => ({
        headerName: showDateShort(date),
        cellRenderer: (params: any) => {
          const playerDates = params.data.preferredDates || [];
          const isChecked = playerDates.includes(date);
          return (
            <input
              type="checkbox"
              checked={isChecked}
              disabled
              className="cursor-not-allowed"
            />
          );
        },
      }));
      newColDefs = [
        ...newColDefs,
        ...dateColumns.map((col) => ({
          ...col,

          filter: false,
          sortable: false,
          flex: 1,
          minWidth: 120,
        })),
      ];
    }

    if (!showPreferredDates) {
      setColumnDefs(newColDefs);
    }


    setColumnDefs(newColDefs);
  }, [currentSeason, showPreferredDates]);

  const handleExport = () => {
    const preferredDates = currentSeason?.preferredDates || [];
    let courses = currentSeason?.courseConfigs.map(
      (course) => course.courseName
    );
    if (courses === undefined) {
      courses = [];
    }
    const playersToExport = players.map((player: any) => {
      return {
        Player: player.fullName,
        Role: player.role,
        Handicap: player.seasonHandicap ?? player.playerHandicap,
        "Home Club": player.homeClub,
        "M/S/L": player.category,
        "Core Member": player.isCoreMember ? "Yes" : "No",
        "Team Name":
          player.playerForTeamName ||
          player.coreMemberForTeamName ||
          player.captainForTeamName ||
          player.ownerForTeamName ||
          "",
        ...preferredDates.reduce((acc: any, date) => {
          acc[showDateShort(date)] = player.preferredDates.includes(date)
            ? "Yes"
            : "No";
          return acc;
        
        }, {}),

        ...courses.reduce((acc: { [key: string]: any }, course) => {
          acc[course] = player[course.toLowerCase().replace(/\s+/g, "")];
          return acc;
        }, {}),
      };
    });
    if (!playersToExport || playersToExport.length === 0) {
      toast.error("No data to export");
      return;
    }
    const dateTimeStamp = formatDateToYYYYMMDDHHMM(new Date());
    exportToCsv(`players_data_${dateTimeStamp}.csv`, playersToExport);
  };

  return (
    <>
      <div className="flex items-center  mt-5  mb-2">
        <div className="relative flex flex-1 h-8 mt-2">
          <label htmlFor="search-field" className="sr-only">
            Search
          </label>
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 dark:text-gray-200"
          />
          <input
            id="search-field"
            name="search"
            type="search"
            placeholder="Search players..."
            onChange={(e) => setSearch(e.target.value)}
            className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 dark:text-gray-200 dark:placeholder:text-gray-200 bg-white dark:bg-gray-600 placeholder:text-gray-400 focus:ring-0 sm:text-sm rounded-md"
          />
          {currentUser &&
            [
              UserRoles.captain,
              UserRoles.nonPlayingTeamOwner,
              UserRoles.playingTeamOwner,
            ].includes(currentUser.role as UserRoles) && (
              <div className="ml-4 flex items-center">
                <input
                  type="checkbox"
                  id="showPreferredDates"
                  checked={showPreferredDates}
                  onChange={(e) => setShowPreferredDates(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="showPreferredDates"
                  className="ml-2 block text-sm text-gray-900 dark:text-gray-200"
                >
                  Preferred Dates
                </label>
              </div>
            )}
        </div>
        {currentUser &&
          [
            UserRoles.captain,
            UserRoles.nonPlayingTeamOwner,
            UserRoles.playingTeamOwner,
          ].includes(currentUser.role as UserRoles) && (
            <Button
              outline
              className="h-8 mx-2 cursor-pointer"
              onClick={handleExport}
            >
              <Download className="h-5 w-5 mr-1" />
              Export
            </Button>
          )}
      </div>
      <div
        className={`${
          isDark ? "ag-theme-alpine-dark" : "ag-theme-alpine"
        } hidden md:block mt-2`}
        style={{ height: `calc(100vh - 220px)`, width: "100%" }}
      >
        <AgGridReact
          rowData={players}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={false}
          rowHeight={70}
          cellSelection={false}
          suppressCellFocus={true}
        />
      </div>
      <div className="block md:hidden mt-2">
        {players &&
          players.map((player: any, index: number) => (
            <div
              key={player.id}
              className={`flex flex-col p-2 mb-1 rounded-lg shadow-md gap-2 ${
                index % 2 === 0
                  ? "bg-gray-100 dark:bg-gray-800"
                  : "bg-white dark:bg-gray-700"
              }`}
            >
              <div className="flex items-center ">
                <div className="h-8 w-11 flex-shrink-0 mr-1">
                  <Avatar
                    firstName={player.firstName}
                    lastName={player.lastName}
                    imageUrl={`/api/static/images/${player.profilePicture}`}
                    className="h-10 w-10 rounded-full"
                  />
                </div>
                <div className="leading-none flex flex-col flex-grow">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {player.fullName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-300">
                    {player.role ? player.role : player.playerRole}
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-300">
                    Home Club: {player.homeClub ? player.homeClub : 'n/a'}, Team:{" "}
                    {player.playerForTeamName ||
                      player.coreMemberForTeamName ||
                      player.captainForTeamName ||
                      player.ownerForTeamName ||
                      "n/a"}
                  </div>
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {getGolfPlayerCategory(player.ageCategory, player.gender)}
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {player.seasonHandicap ?? player.playerHandicap}
                </div>
              </div>
              <div>
                {showPreferredDates && currentSeason && (
                  <>
                    <div className="flex gap-1">
                      {player.preferredDates && (
                        <Text>
                          {player.preferredDates.map(showDateShort).join(", ")}
                        </Text>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
      </div>

      <div className="mt-0 flow-root">
        <div className="pt-1 pb-0 text-center">
          <Text>Showing {players ? players.length : "0"} players.</Text>
        </div>
      </div>
    </>
  );
};
